import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import DateInput from "@components/fields/DateInput";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import useMyClient from "@hooks/useMyClient";
import {
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  ClientRoleRelationshipEnum,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { dateToMDYString } from "@utils/timeUtils";

type FormData = {
  careProfilePlaceId: string | null;
  dateOfBirth?: string | null;
  name: string;
};

type Props = {
  autofocus: boolean;
  journey: GetJourneyWithPlanQuery["journey"];
};

const ClientCareProfileForm = ({ autofocus, journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const myRole = journey.clientRoles.find(role => role.client.id === myClient?.id);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      careProfilePlaceId: yup.string().required(intl.formatMessage({ id: "errors.careProfile.placeId.blank" })),
      dateOfBirth: yup.string().nullable(),
      name: yup.string().required(intl.formatMessage({ id: "errors.careProfile.name.blank" }))
    })
    .required();

  const calculateDefaultValues = useCallback(
    (): FormData => ({
      careProfilePlaceId: journey.careProfile.location.placeId,
      dateOfBirth: journey.careProfile.dateOfBirth ? dateToMDYString(journey.careProfile.dateOfBirth) : null,
      name: journey.careProfile.name ?? ""
    }),
    [journey.careProfile.dateOfBirth, journey.careProfile.location.placeId, journey.careProfile.name]
  );

  const defaultValues = calculateDefaultValues();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const resetForm = useCallback(() => {
    const values = calculateDefaultValues();
    form.reset(values);
  }, [calculateDefaultValues, form]);

  useEffect(() => {
    resetForm();
  }, [resetForm, journey.careProfile.location.placeId, journey.careProfile.name, journey.careProfile.dateOfBirth]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    return updateCareProfile({
      variables: {
        ...form.getValues(),
        id: journey.careProfile.id,
        placeId: form.getValues().careProfilePlaceId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  return (
    <Form data-test="account-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <FormCondition
        condition={myRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.MYSELF}
        fieldName="name"
        form={form}
      >
        <Input<FormData>
          aria-required="true"
          autofocus={autofocus}
          data-test="name-input"
          error={form.formState.errors.name}
          label={intl.formatMessage({ id: "forms.careProfile.nameLabel" })}
          register={form.register("name")}
        />
      </FormCondition>
      <FormCondition
        condition={myRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.MYSELF}
        fieldName="careProfilePlaceId"
        form={form}
      >
        <LocationAutocompleteInput
          control={form.control}
          error={form.formState.errors.careProfilePlaceId}
          label={intl.formatMessage({ id: "forms.careProfile.locationLabel" })}
          labelPlacement="stacked"
          location={{ address: journey.careProfile.location.address, placeId: journey.careProfile.location.placeId }}
          name="careProfilePlaceId"
          noResults={intl.formatMessage({ id: "errors.careProfile.placeId.noResults" })}
          placeholder={intl.formatMessage({ id: "forms.careProfile.locationPlaceholder" })}
          simpleInput={false}
        />
      </FormCondition>
      <DateInput<FormData>
        control={form.control}
        error={form.formState.errors.dateOfBirth}
        label={intl.formatMessage({ id: "forms.careProfileNew.dateOfBirthLabel" })}
        name="dateOfBirth"
        pastOnly
        register={form.register("dateOfBirth")}
      />
      <FormButtons hookForm={form} onCancel={resetForm} skipCancel skipReset />
    </Form>
  );
};

export default ClientCareProfileForm;
